import { Link, router, useForm } from '@inertiajs/react'
import { type FormDataConvertible } from '@inertiajs/core'
import { UserRoundKeyIcon } from 'lucide-react'
import { base64urlToArrayBuffer, arrayBufferToBase64url } from '@/lib/webauthn'
import type { WebAuthnOptions } from '@/types/auth'
import type { ResultError } from '@/types/form'
import AuthLayout from '@/layouts/AuthLayout'
import { AuthCard } from '@/components/AuthCard'
import { FlashBanner } from '@/components/FlashBanner'
import { FormField } from '@/components/FormField'
import { ErrorBanner } from '@/components/ErrorBanner'
import { Input } from '@/components/ui/input'

interface Props {
  webauthn_options?: WebAuthnOptions
  email?: string
  error?: ResultError
}

export default function Login ({ webauthn_options: webauthnOptions, email: initialEmail = '', error }: Props): React.ReactElement {
  const { data, setData, post, processing } = useForm({ email: initialEmail, login: '' })

  async function triggerWebAuthn (options: WebAuthnOptions): Promise<void> {
    try {
      const prepared = prepareGetOptions(options)
      const credential = await navigator.credentials.get({ publicKey: prepared })
      if (credential == null) return
      router.post(
        '/auth/session',
        { assertion: serializeCredential(credential as PublicKeyCredential) as FormDataConvertible },
        { preserveScroll: true }
      )
    } catch (e) {
      console.error('WebAuthn failed:', e)
    }
  }

  function handleSubmit (e: React.SubmitEvent): void {
    e.preventDefault()
    post('/auth/session')
  }

  function handlePasskeySubmit (e: React.SubmitEvent): void {
    e.preventDefault()
    if (webauthnOptions != null) {
      void triggerWebAuthn(webauthnOptions)
    }
  }

  return (
    <AuthLayout>
      <AuthCard>
        <div className='flex items-center gap-2.5 mb-7'>
          <div className='w-9 h-9 bg-(--bp-accent) rounded-lg flex items-center justify-center text-[13px] font-extrabold text-white tracking-tighter'>
            BP
          </div>
          <div className='text-[18px] font-bold tracking-[-0.04em] text-(--bp-accent)'>Blueprint</div>
        </div>

        <div className='text-[22px] font-bold tracking-[-0.04em] text-(--bp-ink) mb-1.5'>ようこそ</div>
        <div className='text-[13px] text-(--bp-ink3) mb-7 leading-[1.55]'>アカウントにログインしてください</div>

        <FlashBanner />

        {(error != null) && <ErrorBanner message={error.error_message} />}

        <form onSubmit={handlePasskeySubmit} style={{ marginBottom: 0 }}>
          <button
            className='flex items-center justify-center gap-2.5 w-full py-3.25 px-5 bg-(--bp-ink) text-white text-sm font-semibold border-none rounded-lg cursor-pointer transition-colors hover:bg-[#0F1826]'
            type='submit'
          >
            <UserRoundKeyIcon width={20} height={20} />
            パスキーでログイン
          </button>
        </form>

        <div className='flex items-center gap-2.5 my-5 text-(--bp-ink3) text-xs'>
          <div className='flex-1 h-px bg-(--bp-border)' />
          <span>または</span>
          <div className='flex-1 h-px bg-(--bp-border)' />
        </div>

        <form onSubmit={handleSubmit}>
          <div className='hidden' aria-hidden='true'>
            <label>
              ログイン
              <Input
                value={data.login}
                onChange={(e) => setData('login', e.target.value)}
                tabIndex={-1}
                autoComplete='off'
              />
            </label>
          </div>
          <FormField label='メールアドレス'>
            <Input
              type='email'
              value={data.email}
              onChange={(e) => setData('email', e.target.value)}
              placeholder='your@email.com'
              autoComplete='email'
              required
            />
          </FormField>
          <button
            className='inline-flex items-center justify-center gap-2 w-full py-2.5 px-5 bg-white text-(--bp-ink) text-sm font-medium border border-(--bp-border-s) rounded-[7px] cursor-pointer transition-all outline-none hover:bg-(--bp-surface2)'
            type='submit'
            disabled={processing}
          >
            メールでリンクを送る
          </button>
        </form>

        <div className='mt-5 text-center text-xs text-(--bp-ink3)'>
          アカウントをお持ちでない方は{' '}
          <Link href='/auth/registration/new' className='text-(--bp-accent) font-medium no-underline'>新規登録</Link> へ
        </div>
      </AuthCard>
    </AuthLayout>
  )
}

function prepareGetOptions (options: WebAuthnOptions): PublicKeyCredentialRequestOptions {
  return {
    challenge: base64urlToArrayBuffer(options.challenge),
    timeout: options.timeout,
    rpId: options.rpId,
    allowCredentials: options.allowCredentials?.map((c) => ({
      id: base64urlToArrayBuffer(c.id),
      type: c.type as PublicKeyCredentialType,
      transports: c.transports as AuthenticatorTransport[] | undefined
    })),
    userVerification: options.userVerification as UserVerificationRequirement | undefined
  }
}

function serializeCredential (credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response as AuthenticatorAssertionResponse
  return {
    id: credential.id,
    rawId: arrayBufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: arrayBufferToBase64url(response.authenticatorData),
      clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
      signature: arrayBufferToBase64url(response.signature),
      userHandle: (response.userHandle != null) ? arrayBufferToBase64url(response.userHandle) : undefined
    }
  }
}
